import { useRecoilState, useRecoilTransaction_UNSTABLE } from 'recoil'
import { useEffect, useState } from 'react'
import { OrganizationFull, Product } from 'api/model'
import { adminOrgsAtom, impersonatedUserAtom, membersByAdminOrgFamily, organizationFamily } from 'state'
import {
  adminListOrgs,
  orgsListMembers,
  productsList,
  usersGetAuthenticated,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useImpersonatingAuthToken } from 'hooks/use-auth-token'
import axios from 'axios'

function useFetchOrgsList() {
  const [adminOrgs, setAdminOrgs] = useRecoilState(adminOrgsAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)

      try {
        const response = await adminListOrgs()
        setAdminOrgs(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [setAdminOrgs])

  return {
    adminOrgs,
    setAdminOrgs,
    isLoading,
    error,
  }
}

function useFetchMembersByOrganizationId(organizationId: string) {
  const [members, setMembers] = useRecoilState(membersByAdminOrgFamily(organizationId))
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      if (!organizationId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await orgsListMembers(organizationId)
        setMembers(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [organizationId, setMembers])

  return {
    members,
    isLoading,
    error,
  }
}

function useFetchImpersonatedUser() {
  const [authToken, setAuthToken] = useImpersonatingAuthToken()
  const [impersonatedUser, setImpersonatedUser] = useRecoilState(impersonatedUserAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const setOrganizations = useRecoilTransaction_UNSTABLE(
    ({ set }) =>
      (organizations: OrganizationFull[]) => {
        organizations.forEach((o) => {
          set(organizationFamily(o.id), (currentOrganization) => Object.assign({}, currentOrganization, o))
        })
      },
    []
  )

  useEffect(() => {
    async function fetchData() {
      if (!authToken) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersGetAuthenticated()
        // we only set organizations / impersonated user when user is not anonymous
        if ('profiles' in response.data) {
          setOrganizations(response.data.profiles.map((up) => up.organization))
          setImpersonatedUser(response.data)
        }
      } catch (error) {
        if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          setAuthToken(null)
        }
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [authToken, setImpersonatedUser, setAuthToken, setOrganizations])

  return {
    impersonatedUser,
    isLoading,
    error,
  }
}

function useFetchProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true)

      try {
        const response = await productsList()
        setProducts(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  return {
    products,
    isLoading,
    error,
  }
}

export { useFetchOrgsList, useFetchImpersonatedUser, useFetchMembersByOrganizationId, useFetchProducts }
